import * as React from 'react';
import { useMemo, useRef, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import Grid from '@mui/material/Unstable_Grid2';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import LinearProgress from '@mui/material/LinearProgress';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { useGetContactsApi, useContactIsDriverApi, useGetLogbookDriversApi, useGetSelectedLogbookReasons } from '../../api/LogbookApi';
import SimpleSnackbar from '../ui/SimpleSnackbar';
import { GetTourTypes, searchUserAsDriver, getFullname, getBreakPoints, compareArrayOfReasons, getLogbookDrivers } from '../other/ConfigBasic';
import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, FormHelperText, useMediaQuery } from '@mui/material';
import { useEffect } from 'react';
import theme from '../../themes/theme';
import { useCallback } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import { v4 as uuidv4 } from 'uuid';
import EditAddress from './EditAddress';
import DialogAddLogbookReason from '../ui/DialogAddLogbookReason';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddIcon from '@mui/icons-material/Add';
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import { isUserCustomerAdmin } from '../auth/authorization';

const ADD_NEW_REASON = { isAddNew: true };
const ADD_NEW_CONTACT = { isAddNew: true };

const buildContactOptions = (contactsData) => {
    if (contactsData === undefined || contactsData === null) {
        return [];
    }

    const contactsArray = contactsData.contactPersons.map(({ firstname, lastname, id }) => ({
        label: `${firstname ?? ''} ${lastname ?? ''}`.trim(),
        value: id,
    }));

    contactsArray.push(
        ...contactsData.contactCompanies.map(({ legalName, id }) => ({
            label: legalName,
            value: id,
        }))
    );

    contactsArray.sort((a, b) => {
        if (a.label.toLowerCase() < b.label.toLowerCase()) {
            return -1;
        }
        if (a.label.toLowerCase() > b.label.toLowerCase()) {
            return 1;
        }
        return 0;
    });

    return contactsArray;
};

const checkDriverData = (logbookDriverData, driverName) => {
    const foundedDriver = logbookDriverData.find((driver) => getFullname(driver) === driverName);
    if (foundedDriver === undefined) {
        logbookDriverData.push({
            id: '123456invalidDriver',
            firstname: driverName,
        });
    }
};

const checkCostCentre = (costCentreList, paramCostCentre) => {
    const foundedCostCentre = costCentreList.find((costcenter) => costcenter.name === paramCostCentre[0]);

    if (foundedCostCentre === undefined) {
        costCentreList.push({
            id: uuidv4(),
            name: paramCostCentre[0],
            active: true,
        });
    }
};

const checkCostObject = (costObjectList, paramCostObject) => {
    const foundedCostObject = costObjectList.find((costObject) => costObject.name === paramCostObject[0]);

    if (foundedCostObject === undefined) {
        costObjectList.push({
            id: uuidv4(),
            name: paramCostObject[0],
            active: true,
        });
    }
};

const getSuggestionIcon = (selectedValue, backendValue, status) => {
    if (status !== 'OPEN') return null;

    if (Array.isArray(backendValue)) {
        if (Array.isArray(selectedValue) && backendValue[0] === selectedValue[0] && backendValue[0])
            return <AutoAwesomeIcon sx={{ color: theme.palette.info.main, marginRight: '4px', fontSize: '20px' }} />;
    }

    if (selectedValue === backendValue && backendValue) return <AutoAwesomeIcon sx={{ color: theme.palette.info.main, marginRight: '4px', fontSize: '20px' }} />;

    return null;
};

export default function EditTourForm(props) {
    const { costCentreList, costObjectList } = props;
    const saveButtonPressed = props.saveButtonPressed;
    const setSaveButtonPressed = props.setSaveButtonPressed;
    const updateEditTourAll = props.updateEditTourAll;
    const userAccess = useOutletContext().userAccess;
    const selectedTour = props.selectedTour;
    const [type, setType] = useState('');
    const [reason, setReason] = useState('');
    const [contact, setContact] = useState('');
    const [costCentre, setCostCentre] = useState('');
    const [costObject, setCostObject] = useState('');
    const [annotation, setAnnotation] = useState('');
    const [selectedDriver, setSelectedDriver] = useState('');
    const [message, setMessage] = useState('');
    const currentLogbook = useOutletContext().logbookId;
    const types = GetTourTypes();
    const desktop900 = useMediaQuery(getBreakPoints().desktop900);
    const { t } = useTranslation();
    const loginUser = useOutletContext().loginUser;
    const [validationErrors, setValidationErrors] = useState({
        driver: false,
        type: false,
        reason: false,
        contact: false,
    });

    const [reasons, setReasons] = useState([]);
    const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
    const reasonInputRef = useRef(null);
    const contactsBeforeAdminRef = useRef(null);
    const lastFetchedContactIdsRef = useRef([]);
    useGetSelectedLogbookReasons(currentLogbook, setMessage, setReasons, loginUser.id);
    const contactsData = useGetContactsApi(loginUser.custNr);
    const contactOptions = useMemo(() => buildContactOptions(contactsData), [contactsData]);
    const canEditContacts = isUserCustomerAdmin(loginUser.adminRoles);
    const allDriverData = useContactIsDriverApi(setMessage);
    const driverIds = useGetLogbookDriversApi(currentLogbook);
    const location = useLocation();

    const handleContactOpen = useCallback(async () => {
        try {
            const refreshed = await contactsData.refetchContacts();
            const freshOptions = buildContactOptions(refreshed);
            lastFetchedContactIdsRef.current = freshOptions.map((option) => option.value);

            const previousIds = contactsBeforeAdminRef.current;

            if (previousIds === null) {
                return;
            }

            //Consume the snapshot on the first click after the admin visit
            contactsBeforeAdminRef.current = null;

            const createdContacts = freshOptions.filter((option) => !previousIds.includes(option.value));

            //Exactly one new contact means it was the one created in the admin, select it.
            //Zero (cancelled) or several (ambiguous) contacts keep the previously typed text.
            if (createdContacts.length === 1) {
                setContact(createdContacts[0].label);
            }
        } catch (error) {
            setMessage(error?.graphQLErrors?.[0]?.message ?? error?.networkError?.message ?? '');
        }
    }, [contactsData, setMessage]);

    const formValidation = useCallback((setValidationErrors, selectedDriver, type, reason, contact, PRIVATE, COMMUTE, TWO_HOUSEHOLDS) => {
        let validationIsSuccessful = true;
        let validationErrors = {
            driver: false,
            type: false,
            reason: false,
            contact: false,
        };

        if (selectedDriver.length === 0) {
            validationErrors.driver = true;
            validationIsSuccessful = false;
        }
        if (type.length === 0) {
            validationErrors.type = true;
            validationIsSuccessful = false;
        }
        if (type !== PRIVATE && type !== COMMUTE && type !== TWO_HOUSEHOLDS) {
            if (reason === undefined || reason.length === 0) {
                validationErrors.reason = true;
                validationIsSuccessful = false;
            }
            if (contact === undefined || contact.length === 0) {
                validationErrors.contact = true;
                validationIsSuccessful = false;
            }
        }

        setValidationErrors(validationErrors);

        return validationIsSuccessful;
    }, []);

    const tourdetails = useCallback((selectedDriver, type, reason, contact, costCentre, costObject, annotation) => {
        return {
            driver: selectedDriver,
            type: type,
            reason: reason,
            contactName: contact,
            costCentre: costCentre,
            costObject: costObject,
            annotation: annotation,
        };
    }, []);

    useEffect(() => {
        if (saveButtonPressed) {
            setSaveButtonPressed(false);
            const editTourFormIsValid = formValidation(setValidationErrors, selectedDriver, type, reason, contact, types.PRIVATE, types.COMMUTE, types.TWO_HOUSEHOLDS);
            if (editTourFormIsValid) {
                if (type === types.PRIVATE || type === types.COMMUTE || type === types.TWO_HOUSEHOLDS) {
                    updateEditTourAll(tourdetails(selectedDriver, type, null, null, null, null, annotation));
                } else if (type === types.OTHER_INCOME) {
                    updateEditTourAll(tourdetails(selectedDriver, type, reason, contact, null, null, annotation));
                } else {
                    updateEditTourAll(tourdetails(selectedDriver, type, reason, contact, costCentre, costObject, annotation));
                }
            }
        }
    }, [
        saveButtonPressed,
        setSaveButtonPressed,
        contact,
        formValidation,
        reason,
        selectedDriver,
        costCentre,
        costObject,
        annotation,
        tourdetails,
        type,
        types.COMMUTE,
        types.PRIVATE,
        types.TWO_HOUSEHOLDS,
        types.OTHER_INCOME,
        updateEditTourAll,
    ]);

    useEffect(() => {
        setSelectedDriver(selectedTour?.driver === null ? '' : selectedTour?.driver);
        setType(selectedTour?.type === null ? '' : selectedTour?.type);
        setReason(selectedTour?.reason === null ? '' : selectedTour?.reason);
        setContact(selectedTour?.contactName === null ? '' : selectedTour?.contactName);
        setCostCentre(selectedTour?.costCentre === null ? '' : selectedTour?.costCentre);
        setCostObject(selectedTour?.costObject === null ? '' : selectedTour?.costObject);
        setAnnotation(selectedTour?.annotation === null ? '' : selectedTour?.annotation);
    }, [selectedTour]);

    useEffect(() => {
        if (location?.state?.type !== undefined) {
            setType(location?.state?.type);
        }
    }, [location]);

    if (!allDriverData || !driverIds || !costCentreList || !costObjectList) {
        return <LinearProgress />;
    }

    let logbookDriverData = getLogbookDrivers(allDriverData, driverIds);

    if (selectedDriver.length === 0) {
        searchUserAsDriver(loginUser.loginUuid, logbookDriverData, setSelectedDriver, false);
    }

    if (selectedTour.driver) {
        checkDriverData(logbookDriverData, selectedTour.driver);
    }

    if (selectedTour.costCentre && costCentre.length > 0) {
        checkCostCentre(costCentreList, costCentre);
    }

    if (selectedTour.costObject && costObject.length > 0) {
        checkCostObject(costObjectList, costObject);
    }

    const handleTypeChange = (event) => {
        setType(event.target.value);
    };

    const handleReasonChange = (event, value) => {
        setReason(value === undefined ? '' : value);
    };

    const openReasonDialog = () => {
        setReasonDialogOpen(true);

        if (reasonInputRef.current !== null) {
            reasonInputRef.current.blur();
        }
    };

    const handleReasonAdded = (addedReason) => {
        if (addedReason === undefined) {
            return;
        }

        if (addedReason.idPerson === null || addedReason.idPerson === loginUser.id) {
            setReasons((previousReasons) => {
                const reasonAlreadyExists = previousReasons.some((reason) => reason.label === addedReason.label && reason.idPerson === addedReason.idPerson);

                if (reasonAlreadyExists) {
                    return previousReasons;
                }

                return previousReasons.concat(addedReason).sort(compareArrayOfReasons);
            });
        }

        setReason(addedReason.label);
    };

    const handleContactChange = (event, value) => {
        setContact(value === undefined ? '' : value);
    };

    const openAdminContacts = () => {
        if (!canEditContacts) {
            return;
        }

        //Snapshot the ids right before leaving to the admin, so the next click
        //after returning can tell which contacts are new
        contactsBeforeAdminRef.current = lastFetchedContactIdsRef.current.length > 0 ? lastFetchedContactIdsRef.current : contactOptions.map((option) => option.value);

        const adminContactsWindow = window.open(process.env.REACT_APP_ADMIN_CONTACTS, '_blank');

        if (adminContactsWindow === null) {
            contactsBeforeAdminRef.current = null;
            setMessage(t('contactPopupBlocked'));
        }
    };

    const handleCostCentreChange = (event) => {
        setCostCentre([event.target.value]);
    };

    const handleCostObjectChange = (event) => {
        setCostObject([event.target.value]);
    };

    const handleAnnotationChange = (event) => {
        setAnnotation(event.target.value);
    };

    const handleDriverChange = (event) => {
        setSelectedDriver(event.target.value);
    };

    const disableElement = () => {
        let disableElement = true;
        if ((selectedTour.status === 'OPEN' || (selectedTour.status === 'EDITED' && selectedTour.type !== 'PRIVATE')) && userAccess < 3) {
            disableElement = false;
        }
        return disableElement;
    };

    const disableElementTypeOfTour = () => {
        let disableElement = true;
        if (selectedTour.type !== 'PRIVATE' && userAccess < 3) {
            disableElement = false;
        }
        return disableElement;
    };

    const getTranslation = (key) => {
        let translation = '';
        switch (key) {
            case types.BUSINESS:
                translation = t('business');
                break;
            case types.COMMUTE:
                translation = t('commute');
                break;
            case types.OTHER_INCOME:
                translation = t('otherIncome');
                break;
            case types.PRIVATE:
                translation = t('privateTour');
                break;
            case types.TWO_HOUSEHOLDS:
                translation = t('twoHouseholds');
                break;
            default:
                break;
        }
        return translation;
    };

    const sortAndGetTypes = () => {
        let typesWithTranslation = [];
        Object.values(types).map((type) =>
            typesWithTranslation.push({
                type: type,
                translation: getTranslation(type),
            })
        );
        typesWithTranslation.sort((a, b) => {
            if (a.translation?.toLocaleUpperCase() < b.translation?.toLocaleUpperCase() || b.translation === null) {
                return -1;
            } else if (a.translation?.toLocaleUpperCase() > b.translation?.toLocaleUpperCase() || a.translation === null) {
                return 1;
            } else {
                return 0;
            }
        });
        return typesWithTranslation;
    };
    return (
        <>
            {props.showEditAddress && (
                <EditAddress
                    mobile={desktop900}
                    open={props.showEditAddress}
                    setOpen={props.setShowEditAddress}
                    startInfos={props.startInfos}
                    endInfos={props.endInfos}
                    setMessage={setMessage}
                    tour={selectedTour}
                    setAnnotation={setAnnotation}
                />
            )}
            <Card variant='outlined'>
                <CardContent>
                    <Grid item xs={12} md={12} padding={theme.components.LogbookFormGrid.padding}>
                        <FormControl fullWidth error={validationErrors.driver} required={true}>
                            <InputLabel id='demo-simple-select-label'>{t('driver')}</InputLabel>
                            <Select
                                disabled={disableElement()}
                                labelId='demo-simple-select-label'
                                id='demo-simple-select'
                                value={selectedDriver}
                                label={t('driver')}
                                onChange={handleDriverChange}
                            >
                                {logbookDriverData.map((driver) => (
                                    <MenuItem key={driver.id} value={getFullname(driver)}>
                                        {getFullname(driver)}
                                    </MenuItem>
                                ))}
                            </Select>
                            {validationErrors.driver && <FormHelperText>{t('mandatoryField')}</FormHelperText>}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={12} padding={theme.components.LogbookFormGrid.padding}>
                        <FormControl fullWidth error={validationErrors.type} required={true}>
                            <InputLabel id='demo-simple-select-label'>{t('typeOfTour')}</InputLabel>
                            <Select
                                disabled={disableElementTypeOfTour()}
                                labelId='demo-simple-select-label'
                                id='demo-simple-select'
                                value={type}
                                label={t('typeOfTour')}
                                onChange={handleTypeChange}
                            >
                                {sortAndGetTypes().map((type) => (
                                    <MenuItem key={type.type} value={type.type}>
                                        {type.translation}
                                    </MenuItem>
                                ))}
                            </Select>
                            {validationErrors.type && <FormHelperText>{t('mandatoryField')}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    {type !== types.PRIVATE && type !== types.COMMUTE && type !== types.TWO_HOUSEHOLDS && (
                        <>
                            <Grid item xs={12} md={12} padding={theme.components.LogbookFormGrid.padding}>
                                <FormControl fullWidth error={validationErrors.reason}>
                                    <Autocomplete
                                        ref={reasonInputRef}
                                        disabled={disableElement()}
                                        freeSolo
                                        inputValue={reason === undefined ? '' : reason}
                                        options={Array.from(
                                            new Set(
                                                [...reasons]
                                                    .sort((a, b) => {
                                                        if (a.label?.toLocaleUpperCase() < b.label?.toLocaleUpperCase() || b.label === null) {
                                                            return -1;
                                                        } else if (a.label?.toLocaleUpperCase() > b.label?.toLocaleUpperCase() || a.label === null) {
                                                            return 1;
                                                        } else {
                                                            return 0;
                                                        }
                                                    })
                                                    .map((reason) => (reason.label === null ? '' : reason.label.trim()))
                                                    .filter((label) => label.length > 0)
                                            )
                                        )}
                                        getOptionLabel={(option) => (option?.isAddNew ? (reason === undefined ? '' : reason) : option)}
                                        filterOptions={(options, state) => {
                                            const typedReason = state.inputValue.trim();

                                            if (typedReason.length === 0 || disableElement()) {
                                                return options;
                                            }

                                            const matchingOptions = options.filter((option) => option.toLowerCase().includes(typedReason.toLowerCase()));
                                            const hasExactMatch = matchingOptions.some((option) => option.toLowerCase() === typedReason.toLowerCase());

                                            if (hasExactMatch) {
                                                return matchingOptions;
                                            }

                                            return [ADD_NEW_REASON, ...matchingOptions];
                                        }}
                                        onChange={(event, option) => {
                                            if (option?.isAddNew) {
                                                openReasonDialog();
                                            }
                                        }}
                                        renderOption={(props, option) => {
                                            if (option?.isAddNew) {
                                                return (
                                                    <Box
                                                        component='li'
                                                        {...props}
                                                        key='add-new-reason'
                                                        onMouseDown={(event) => event.preventDefault()}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            openReasonDialog();
                                                        }}
                                                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}
                                                    >
                                                        <Box
                                                            component='span'
                                                            sx={{
                                                                flex: '1 1 auto',
                                                                minWidth: 0,
                                                                overflow: 'hidden',
                                                                whiteSpace: 'nowrap',
                                                                textOverflow: 'ellipsis',
                                                            }}
                                                        >
                                                            {reason}
                                                        </Box>
                                                        <Tooltip title={t('addNewTooltip')} placement='left' enterDelay={500}>
                                                            <Box
                                                                component='span'
                                                                sx={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    flexShrink: 0,
                                                                    gap: '4px',
                                                                    color: theme.palette.primary.main,
                                                                    fontFamily: 'CircularProBold',
                                                                    fontSize: '14px',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                <AddIcon sx={{ fontSize: '18px' }} />
                                                                {t('addNew')}
                                                            </Box>
                                                        </Tooltip>
                                                    </Box>
                                                );
                                            }

                                            return (
                                                <li {...props} key={option}>
                                                    {option}
                                                </li>
                                            );
                                        }}
                                        onInputChange={handleReasonChange}
                                        renderInput={(params) => (
                                            <TextField
                                                error={validationErrors.reason}
                                                required={true}
                                                {...params}
                                                label={t('reasonOfTheTour')}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            {getSuggestionIcon(reason, selectedTour.reason, selectedTour.status)}
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                    {validationErrors.reason && <FormHelperText>{t('mandatoryField')}</FormHelperText>}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={12} padding={theme.components.LogbookFormGrid.padding}>
                                <FormControl fullWidth error={validationErrors.contact}>
                                    <Autocomplete
                                        disabled={disableElement()}
                                        onOpen={handleContactOpen}
                                        freeSolo
                                        inputValue={contact === undefined ? '' : contact}
                                        options={contactOptions}
                                        getOptionLabel={(contact) => (contact?.isAddNew ? t('noResult') : contact.label)}
                                        filterOptions={(options, state) => {
                                            const inputValue = state.inputValue.toLowerCase();
                                            const firstNameStarts = [];
                                            const lastNameStarts = [];
                                            const others = [];
                                            options.forEach((opt) => {
                                                const [firstName, ...rest] = opt.label.split(' ');
                                                const lastName = rest.join(' ');
                                                if (firstName.toLowerCase().startsWith(inputValue)) {
                                                    firstNameStarts.push(opt);
                                                } else if (lastName.toLowerCase().startsWith(inputValue)) {
                                                    lastNameStarts.push(opt);
                                                } else if (opt.label.toLowerCase().includes(inputValue)) {
                                                    others.push(opt);
                                                }
                                            });
                                            const matchingOptions = [...firstNameStarts, ...lastNameStarts, ...others];

                                            if (state.inputValue.trim().length > 0 && !disableElement() && canEditContacts && matchingOptions.length === 0) {
                                                return [ADD_NEW_CONTACT];
                                            }

                                            return matchingOptions;
                                        }}
                                        onChange={(event, option) => {
                                            if (option?.isAddNew) {
                                                openAdminContacts();
                                            }
                                        }}
                                        renderOption={(props, option) => {
                                            if (option?.isAddNew) {
                                                return (
                                                    <Box
                                                        component='li'
                                                        {...props}
                                                        key='add-new-contact'
                                                        onMouseDown={(event) => event.preventDefault()}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            openAdminContacts();
                                                        }}
                                                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}
                                                    >
                                                        <Box
                                                            component='span'
                                                            sx={{
                                                                flex: '1 1 auto',
                                                                minWidth: 0,
                                                                overflow: 'hidden',
                                                                whiteSpace: 'nowrap',
                                                                textOverflow: 'ellipsis',
                                                            }}
                                                        >
                                                            {t('noResult')}
                                                        </Box>
                                                        <Tooltip title={t('addNewContactTooltip')} placement='left' enterDelay={500}>
                                                            <Box
                                                                component='span'
                                                                onMouseDown={(event) => event.preventDefault()}
                                                                sx={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    flexShrink: 0,
                                                                    gap: '4px',
                                                                    color: theme.palette.primary.main,
                                                                    fontFamily: 'CircularProBold',
                                                                    fontSize: '14px',
                                                                    cursor: 'pointer',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                <AddIcon sx={{ fontSize: '18px' }} />
                                                                {t('addNew')}
                                                            </Box>
                                                        </Tooltip>
                                                    </Box>
                                                );
                                            }

                                            return (
                                                <li {...props} key={option.value}>
                                                    {option.label}
                                                </li>
                                            );
                                        }}
                                        onInputChange={handleContactChange}
                                        renderInput={(params) => (
                                            <TextField
                                                error={validationErrors.contact}
                                                required={true}
                                                {...params}
                                                label={t('visitedContact')}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            {getSuggestionIcon(contact, selectedTour.contactName, selectedTour.status)}
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                    {validationErrors.contact && <FormHelperText>{t('mandatoryField')}</FormHelperText>}
                                </FormControl>
                            </Grid>
                        </>
                    )}

                    {(costCentreList.length > 0 || costObjectList.length > 0) && type === types.BUSINESS && (
                        <>
                            <Grid item xs={12} md={12} padding={theme.components.LogbookFormGrid.padding}>
                                <FormControl fullWidth>
                                    <InputLabel id='demo-simple-select-label'>{t('costCentre')}</InputLabel>
                                    <Select
                                        disabled={disableElement()}
                                        labelId='demo-simple-select-label'
                                        id='demo-simple-select'
                                        value={costCentre === '' ? costCentre : costCentre[0]}
                                        onChange={handleCostCentreChange}
                                        input={
                                            <OutlinedInput
                                                label={t('costCentre')}
                                                startAdornment={
                                                    <InputAdornment position='start' style={{ marginLeft: '-4px' }}>
                                                        {getSuggestionIcon(costCentre, selectedTour.costCentre, selectedTour.status)}
                                                    </InputAdornment>
                                                }
                                            />
                                        }
                                        endAdornment={
                                            costCentre.length > 0 &&
                                            userAccess < 3 && (
                                                <ClearIcon
                                                    sx={{ cursor: 'pointer', backgroundColor: '#fff', zIndex: 1, color: theme.palette.grey[900], marginRight: '-7px' }}
                                                    onClick={() => {
                                                        setCostCentre('');
                                                    }}
                                                />
                                            )
                                        }
                                    >
                                        {costCentreList.map((costCentre) => (
                                            <MenuItem key={costCentre.id} value={costCentre.name}>
                                                {costCentre.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={12} padding={theme.components.LogbookFormGrid.padding}>
                                <FormControl fullWidth>
                                    <InputLabel id='demo-simple-select-label'>{t('costObject')}</InputLabel>
                                    <Select
                                        disabled={disableElement()}
                                        labelId='demo-simple-select-label'
                                        id='demo-simple-select'
                                        value={costObject === '' ? costObject : costObject[0]}
                                        onChange={handleCostObjectChange}
                                        input={
                                            <OutlinedInput
                                                label={t('costObject')}
                                                startAdornment={
                                                    <InputAdornment position='start' style={{ marginLeft: '-4px' }}>
                                                        {getSuggestionIcon(costObject, selectedTour.costObject, selectedTour.status)}
                                                    </InputAdornment>
                                                }
                                            />
                                        }
                                        endAdornment={
                                            costObject.length > 0 &&
                                            userAccess < 3 && (
                                                <ClearIcon
                                                    sx={{ cursor: 'pointer', backgroundColor: '#fff', zIndex: 1, color: theme.palette.grey[900], marginRight: '-7px' }}
                                                    onClick={() => {
                                                        setCostObject('');
                                                    }}
                                                />
                                            )
                                        }
                                    >
                                        {costObjectList.map((costObject) => (
                                            <MenuItem key={costObject.id} value={costObject.name}>
                                                {costObject.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12} md={12} padding={theme.components.LogbookFormGrid.padding}>
                        <TextField
                            disabled={disableElement()}
                            fullWidth
                            id=''
                            label={t('annotation')}
                            variant='outlined'
                            value={annotation}
                            onChange={handleAnnotationChange}
                            multiline
                            maxRows={10}
                            minRows={2}
                            size='small'
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start' style={{ marginLeft: '-4px' }}>
                                        {getSuggestionIcon(annotation, selectedTour.annotation, selectedTour.status)}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    <DialogAddLogbookReason
                        open={reasonDialogOpen}
                        onClose={() => {
                            setReasonDialogOpen(false);

                            if (reasonInputRef.current !== null) {
                                reasonInputRef.current.focus();
                            }
                        }}
                        initialLabel={reason}
                        onAdded={(addedReason) => handleReasonAdded(addedReason)}
                        setMessage={setMessage}
                    />

                    {message && <SimpleSnackbar message={message} setMessage={setMessage} />}
                </CardContent>
            </Card>
        </>
    );
}
